/**
 * Insights to TreeNode Converter
 *
 * Converts InsightsAnalysis documents to 3-level tree structure for rc-tree:
 * Analysis → JTBD Category → Individual Insight Item
 */

import type { InsightsTreeNode, InsightsAnalysisForPicker, JtbdCategory, AggregatedInsightItem } from './types';
import { JTBD_CATEGORIES, JTBD_CATEGORY_LABELS } from './types';
import {
  averageAudienceScore,
  formatReadiness,
  getB2BAudienceMetrics,
} from '@modules/insights-analyzer/utils/b2bAudienceMetrics';

/**
 * Builds a display label for an insight item
 */
function insightItemLabel(item: AggregatedInsightItem): string {
  const desc = item.canonicalDescription
    ? item.canonicalDescription.substring(0, 80) + (item.canonicalDescription.length > 80 ? '...' : '')
    : 'Unnamed insight';
  const weight = item.cumulativeWeight != null ? ` (w: ${item.cumulativeWeight.toFixed(1)})` : '';
  return `${desc}${weight}`;
}

/**
 * Counts total insights across all categories in an analysis
 */
export function countTotalInsights(analysis: InsightsAnalysisForPicker): number {
  if (!analysis.aggregatedInsights) return 0;
  return Object.values(analysis.aggregatedInsights).reduce(
    (sum, items) => sum + (Array.isArray(items) ? items.length : 0),
    0,
  );
}

function b2bAudienceSummary(analysis: InsightsAnalysisForPicker): string {
  const metrics = getB2BAudienceMetrics(analysis);
  if (analysis.mode !== 'b2b' && metrics.length === 0) return '';

  const primary = metrics.find((metric) => metric.score != null || metric.readerDescription) ?? metrics[0];
  const score = averageAudienceScore(metrics);
  const reader = primary?.readerDescription ?? primary?.primaryReaderRole;
  const readiness = primary ? formatReadiness(primary) : '';
  const count = metrics.length;
  return [
    'B2B',
    count ? `${count} audience${count === 1 ? '' : 's'}` : '',
    score != null ? `score: ${score}/100` : '',
    reader ? `who: ${reader}` : '',
    readiness ? `readiness: ${readiness}` : '',
  ].filter(Boolean).join(' · ');
}

/**
 * Converts InsightsAnalysis documents to a 3-level tree structure.
 *
 * Tree layout:
 * ```
 * [x] "Fitness App Analysis" (42 insights)
 *   [x] Core Jobs (12)
 *     [x] "Lose weight and feel better" (w: 8.5)
 *     [ ] "Track calories daily" (w: 6.2)
 *   [x] Fears (8)
 *     [x] "Yo-yo dieting" (w: 7.1)
 *   [ ] Contexts (6)
 *     ...
 * ```
 */
export function insightsToTreeNodes(
  analyses: InsightsAnalysisForPicker[],
): InsightsTreeNode[] {
  return analyses.map((analysis) => {
    const totalInsights = countTotalInsights(analysis);
    const categoryChildren: InsightsTreeNode[] = [];

    if (analysis.aggregatedInsights) {
      for (const cat of JTBD_CATEGORIES) {
        const items = analysis.aggregatedInsights[cat];
        if (!Array.isArray(items) || items.length === 0) continue;

        // Deduplicate items by id within category to prevent duplicate rc-tree keys
        const seenIds = new Set<string>();
        const uniqueItems = items.filter((item) => {
          if (seenIds.has(item.id)) return false;
          seenIds.add(item.id);
          return true;
        });

        const insightChildren: InsightsTreeNode[] = uniqueItems.map((item) => ({
          key: `insight-${analysis.id}-${cat}-${item.id}`,
          title: insightItemLabel(item),
          nodeType: 'item' as const,
          analysisId: analysis.id,
          category: cat as JtbdCategory,
          insightId: item.id,
          checkable: true,
          selectable: false,
        }));

        categoryChildren.push({
          key: `cat-${analysis.id}-${cat}`,
          title: `${JTBD_CATEGORY_LABELS[cat]} (${uniqueItems.length})`,
          nodeType: 'category' as const,
          analysisId: analysis.id,
          category: cat as JtbdCategory,
          checkable: true,
          selectable: false,
          children: insightChildren,
        });
      }
    }

    const summary = b2bAudienceSummary(analysis);
    return {
      key: `analysis-${analysis.id}`,
      title: `${analysis.name || 'Unnamed Analysis'} (${totalInsights} insights)${summary ? ` — ${summary}` : ''}`,
      nodeType: 'analysis' as const,
      analysisId: analysis.id,
      checkable: true,
      selectable: false,
      children: categoryChildren,
    };
  });
}
